import { useRef, useEffect, useMemo } from 'react';

import BulkUpload from '@Components/BasicComponents/BulkUpload';
import Validate from '@Components/Validate';

import {
	filesUploaded,
	optionalFilesUploaded,
} from '@/utils/errors/list';
import {
	checkVideoNames,
	checkSubtitleNames,
	checkVideoThumbnailNames,
} from '@/utils/names';
import { getSetting } from '@/utils/settings';

import useIbmS3Uploader from '@/hooks/useIbmS3UploaderNew';
import useNotification from '@/hooks/notifications';

const videoTooltip =
	'Video files must use the naming convention {referenceID}[{lang}-{res}].mp4 or .mov. English (EN) is mandatory. Resolution must be one of: 8K, UHD, HD, HIGH, LOW.';

const subtitlesTooltip =
	'Subtitle files must use the naming convention {referenceID}[{lang}-subt].srt or .vtt. If any subtitle is uploaded, English is required.';

const thumbnailTooltip =
	'Custom thumbnail must use the naming convention {referenceID}[thumb].jpg or .jpeg.';

const videoRequirements = [filesUploaded];
const subtitlesRequirements = [optionalFilesUploaded];
const thumbnailRequirements = [optionalFilesUploaded];

export default function VideoUpload ({selector}) {
	const {
		files,
		epId,
		setFile,
		removeFileByUid,
	} = selector();

	const {openNotification} = useNotification();

	const videoUploader = useRef(
		useIbmS3Uploader({
			setFile,
			removeFile: removeFileByUid,
			bucketPrefix: epId.data + '/',
			bucketName: getSetting('ibmS3TempBucket'),
		})
	);

	const subtitlesUploader = useRef(
		useIbmS3Uploader({
			setFile,
			removeFile: removeFileByUid,
			bucketPrefix: epId.data + '/',
			bucketName: getSetting('ibmS3TempBucket'),
		})
	);

	const thumbnailUploader = useRef(
		useIbmS3Uploader({
			setFile,
			removeFile: removeFileByUid,
			bucketPrefix: epId.data + '/',
			bucketName: getSetting('ibmS3TempBucket'),
		})
	);

	const {handleUpload: handleVideoUpload, updateBucketPrefix: updateVideoBucket, handleRemove: handleVideoRemove} = videoUploader.current;
	const {handleUpload: handleSubtitlesUpload, updateBucketPrefix: updateSubtitlesBucket, handleRemove: handleSubtitlesRemove} = subtitlesUploader.current;
	const {handleUpload: handleThumbnailUpload, updateBucketPrefix: updateThumbnailBucket, handleRemove: handleThumbnailRemove} = thumbnailUploader.current;

	useEffect(() => {
		updateVideoBucket(epId.data + '/');
		updateSubtitlesBucket(epId.data + '/');
		updateThumbnailBucket(epId.data + '/');
	}, [updateVideoBucket, updateSubtitlesBucket, updateThumbnailBucket, epId.data]);

	console.log(files);
	
	const videoFiles = useMemo(() => (files || []).filter((f) => f.format === 'mp4' || f.format === 'mov'), [files]);
	const subtitleFiles = useMemo(() => (files || []).filter((f) => f.format === 'srt' || f.format === 'vtt'), [files]);
	const thumbnailFiles = useMemo(() => (files || []).filter((f) => f.format === 'jpg' || f.format === 'jpeg'), [files]);

	const videoBeforeUpload = (file) => checkVideoNames({id: epId.data, openNotification})(file);
	const subtitleBeforeUpload = (file) => checkSubtitleNames({id: epId.data, openNotification})(file);
	const thumbnailBeforeUpload = (file) => checkVideoThumbnailNames({id: epId.data, openNotification})(file);

	return (
		<>
			<Validate requirements={videoRequirements}>
				<BulkUpload
					title="Video files upload *"
					value={videoFiles}
					handleUpload={({file}) => handleVideoUpload({file})}
					beforeUpload={videoBeforeUpload}
					handleRemove={({uid}) => handleVideoRemove({uid})}
					accept=".mp4,.mov"
					tooltip={videoTooltip}
					required
					allowRemoveOnErrorOnly
				/>
			</Validate>
			<br />
			<br />
			<Validate requirements={subtitlesRequirements}>
				<BulkUpload
					title="Subtitles / transcriptions upload"
					value={subtitleFiles}
					handleUpload={({file}) => handleSubtitlesUpload({file})}
					beforeUpload={subtitleBeforeUpload}
					handleRemove={({uid}) => handleSubtitlesRemove({uid})}
					accept=".srt,.vtt"
					tooltip={subtitlesTooltip}
					allowRemoveOnErrorOnly
				/>
			</Validate>
			<br />
			<br />
			<Validate requirements={thumbnailRequirements}>
				<BulkUpload
					title="Custom thumbnail upload"
					value={thumbnailFiles}
					handleUpload={({file}) => handleThumbnailUpload({file})}
					beforeUpload={thumbnailBeforeUpload}
					handleRemove={({uid}) => handleThumbnailRemove({uid})}
					accept="image/jpeg,.jpg,.jpeg"
					tooltip={thumbnailTooltip}
					allowRemoveOnErrorOnly
				/>
			</Validate>
		</>
	);
}
